/* eslint-disable no-unused-vars, max-len*/
// Core angular modules
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { LocationStrategy, PercentPipe } from '@angular/common';
import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';

// Third Party
import { CookieService } from 'angular2-cookie/core';
import { NgReduxModule, DevToolsExtension } from '@angular-redux/store';

// Platform
import { ENV_PROVIDERS } from '../../../platform/environment';
import { ErrorSubmittingAppComponent } from '../../routes/+error-submitting-app';
import { RejectedAppComponent } from '../../routes/+rejected-app';
import { ROUTES } from './root.routes';

// Routes
import { AccountTypeComponent } from '../../routes/+account-type';
import { AchProfileComponent } from '../../routes/+ach-profile';
import { AchProfileFailedComponent } from '../../routes/+ach-profile-failed';
import { CashAllocationComponent } from '../../routes/+cash-allocation';
import { CipComponent } from '../../routes/+cip';
import { CredentialsComponent } from '../../routes/+credentials';
import { CustodialMinorInfoComponent } from '../../routes/+custodial-minor-info';
import { DepositFundsComponent } from '../../routes/+deposit-funds';
import { DepositFundsReceiptComponent } from '../../routes/+deposit-funds-receipt';
import { EditBeneficiaryInfoComponent } from '../../routes/+edit-beneficiary-info';
import { EmploymentInfoComponent } from '../../routes/+employment-info';
import { FinancialInfoComponent } from '../../routes/+financial-info';
import { FinishPaperAccountComponent } from '../../routes/+finish-paper-account';
import { FundingChoicesComponent } from '../../routes/+funding-choices';
import { FundAbaRoutingComponent } from '../../routes/+fund-aba-routing';
import { FundingChoicesStreamlineComponent } from '../../routes/+funding-choices-streamline';
import { FundAccountComponent } from '../../routes/+fund-account';
import { FundByBrokerComponent } from '../../routes/+fund-by-broker';
import { FundByBrokerChooseFirmComponent } from '../../routes/+fund-by-broker-choose-firm';
import { FundByBrokerAccountComponent } from '../../routes/+fund-by-broker-account';
import { FundByBrokerInfoComponent } from '../../routes/+fund-by-broker-info';
import { FundByBrokerManualComponent } from '../../routes/+fund-by-broker-manual';
import { FundByBrokerReviewComponent } from '../../routes/+fund-by-broker-review';
import { FundByBrokerReceiptComponent } from '../../routes/+fund-by-broker-receipt';
import { FundByCheckComponent } from '../../routes/+fund-by-check';
import { FundByInvestComponent } from '../../routes/+fund-by-invest-account';
import { FundByWireComponent } from '../../routes/+fund-by-wire';
import { GetStartedComponent } from '../../routes/+get-started';
import { InvestmentObjectiveComponent } from '../../routes/+investment-objective';
import { InvestmentPreferencesComponent } from '../../routes/+investment-preferences';
import { TrustedContactComponent } from '../../routes/+trusted-contact';
import { JointFinancialInfoComponent } from '../../routes/+joint-financial-info';
import { JointPersonalInfoComponent } from '../../routes/+joint-personal-info';
import { ManualComponent } from '../../routes/+manual';
import { ModalComponent } from '../../routes/+modal';
import { PaperAccountComponent } from '../../routes/+paper-account';
import { PersonalInfoComponent } from '../../routes/+personal-info';
import { TimeOutComponent } from '../../routes/+time-out';
import { WelcomeInvestorComponent } from '../../routes/+welcome-investor';
import { GoalComponent } from '../../routes/+goal';
import { TimeHorizonComponent } from '../../routes/+time-horizon';
import { FinancialPictureComponent } from '../../routes/+financial-picture';
import { RiskToleranceComponent } from '../../routes/+risk-tolerance';
import { StrategyTypeComponent } from '../../routes/+strategy-type';
import { RecommendationComponent } from '../../routes/+recommendation';
import { RetirementComponent } from '../../routes/+retirement';
import { DocumentUploaderComponent } from '../../routes/+document-uploader';
import { DocumentConfirmationReceivedComponent } from '../../routes/+document-confirmation-received';
import { DocumentCheckYourInboxComponent } from '../../routes/+document-check-your-inbox';
import { WelcomeBackComponent } from '../../routes/+welcome-back';

// For Personal Info A/B test
import { PersonalAddressComponent } from '../../routes/+personal-address';
import { PersonalContactComponent } from '../../routes/+personal-contact';
import { SSNComponent } from '../../routes/+ssn';

import { PersonalInfoAuthComponent } from '../../routes/+personal-info-auth';
import { PersonalInfoACMComponent } from '../../routes/+personal-info-acm';
import { PendingAppComponent } from '../../routes/+pending-app';
import { PendingAppCipComponent } from '../../routes/+pending-app-cip';
import { PrimaryBeneficiaryInfoComponent } from '../../routes/+primary-beneficiary-info';
import { ReviewDepositFundsComponent } from '../../routes/+review-deposit-funds';
import { RootComponent } from './root.component';
import { SubmitApplicationComponent } from '../../routes/+submit-application';
import { SubmitApplicationReviewComponent } from '../../routes/+submit-application-review';
import { SuccessComponent } from '../../routes/+success';
import { WaysToFundComponent } from '../../routes/+ways-to-fund';

// Resolvers/Guards
import { DataResolver } from '../../resolvers/data.resolver';
import { DTMGuard } from '../../guards/dtm.guard';
import { SubmitAppCanActivate } from '../../guards/submitApp.guard';
import { CredentialsCanActivate } from '../../guards/credentials.guard';
import { ACHProfileCanActivate } from '../../guards/achProfile.guard';
import { ACHTransferCanActivate } from '../../guards/achTransfer.guard';
import { GetKBACanActivate } from '../../guards/getKBA.guard';
import { DTMService } from '../../shared/services/dtm.service';
import { DepositAccountsResolver } from '../../resolvers/depositAccounts.resolver';
import { HandshakeCanActivate } from '../../guards/handshake.guard';
import { ReviewResolver } from '../../resolvers/review.resolver';

import { ModelCanDeactivate } from '../../guards/modal.guard';
import { ValidationCanDeactivate } from '../../guards/validation.guard';
import { SubmitAcatsCanActivate } from '../../guards/submitAcats.guard';
import { MpQuestionnaireCanActivate } from '../../guards/mpQuestionnaire.guard';

// Services
import { ApplicationProxyService } from '../../shared/services/applicationproxy.service';
import { BadWordsService } from '../../shared/services/words.service';
import { DomInjectorService } from '../../shared/services/domInjector.service';
import { FlowManagerService } from '../../shared/services/flowmanager.service';
import { TransmitService } from '../../shared/services/transmit.service';
import { FundingService } from '../../shared/services/funding.service';
import { LocalStorageService } from '../../shared/services/localstorage.service';
import { LoggerService } from '../../shared/services/logger.service';
import { LogRocketTrackerService } from '../../shared/services/log-rocket-tracker.service';
import { MaskingService } from '../../shared/services/masking.service';
import { ModalService } from '../../shared/services/modal.service';
import { PlaidService } from '../../shared/services/plaid.service';
import { RestService } from '../../shared/services/rest.service';
import { SelectOptionsService } from '../../shared/services/selectoptions.service';
import { SessionStorageService } from '../../shared/services/sessionstorage.service';
import { StartUpService } from '../../shared/services/startup.service';
import { TimeOutService } from '../../shared/services/timeout.service';
import { TimeService } from '../../shared/services/time.service';
import { Validation } from '../../shared/services/validation.service';
import { RouteParamsHandler } from '../../shared/services/routeParamsHandler.service';
import { QuestionnaireService } from '../../shared/services/questionnaire.service';
import { ScreenSizeService } from '../../shared/services/screensize.service';
import { GoalValidationService } from '../../shared/services/goalvalidation.service';

//  Components
import { ActionHelperComponent } from '../actionHelper/actionHelper.component';
import { CheckboxComponent } from '../checkbox/checkbox.component';
import { CaretArrowComponent } from '../svg/caret-arrow/caret-arrow.component';
import { CurrencyComponent } from '../currency/currency.component';
import { DisclaimerComponent } from '../disclaimer/disclaimer.component';
import { EmploymentfactoryComponent } from '../employmentFactory/employmentFactory.component';
import { FaqComponent } from '../faq/faq.component';
import { FaqAnswerComponent } from '../faq/faq-answer/faq-answer.component';
import { FieldDisplayComponent } from '../fieldDisplay/fieldDisplay.component';
import { FooterComponent } from '../footer/footer.component';
import { FormfactoryComponent } from '../formfactory/formfactory.component';
import { FormAccountTypeHeaderComponent } from '../formAccountTypeHeader/formAccountTypeHeader.component';
import { FundingFooterComponent } from '../fundingFooter/fundingFooter.component';
import { HeaderComponent } from '../header/header.component';
import { HelpComponent } from '../help/help.component';
import { HelpTipComponent } from '../helpTip/helpTip.component';
import { IconComponent } from '../svg/icon/icon.component';
import { InputComponent } from '../input/input.component';
import { ManualDocsComponent } from '../manualDocs/manualDocs.component';
import { NoneditableComponent } from '../noneditable/noneditable.component';
import { PageErrorComponent } from '../pageError/pageError.component';
import { PrivacyComponent } from '../privacy/privacy.component';
import { ProgressBarComponent } from '../progress-bar/progress-bar.component';
import { RadioComponent } from '../radio/radio.component';
import { SelectComponent } from '../select/select.component';
import { ShowHidefactoryComponent } from '../showHideFactory/showHideFactory.component';
import { StepTitleComponent } from '../stepTitle/stepTitle.component';
import { SvgLoaderComponent } from '../svg/loader/loader.component';
import { TextareaComponent } from '../textarea/textarea.component';
import { TooltipComponent } from '../tooltip/tooltip.component';
import { TypeAheadSearchComponent } from '../typeAheadSearch/typeAheadSearch.component';
import { ValuepropComponent } from '../valueprop/valueprop.component';
import { IconButtonComponent } from '../icon-button/icon-button.component';
import { IconRadioButtonComponent } from '../icon-radio-button/icon-radio-button.component';
import { RiskButtonComponent } from '../risk-button/risk-button.component';
import { QuestionnaireRightRailComponent } from '../questionnaire-right-rail/questionnaire-right-rail.component';
import { QuestionnaireTitleComponent } from '../questionnaire-title/questionnaire-title.component';
import { HelpMeDecideComponent } from '../help-me-decide/help-me-decide.component';
import { AssetBreakdownComponent } from '../asset-breakdown/asset-breakdown.component';
import { CardComponent } from '../card/card.component';
import { AddressComponent } from '../address/address.component';
import { CashVestedComponent } from '../cash-vested/cash-vested.component';
import { PortfolioAllocationsTableComponent } from '../portfolio-allocations-table/portfolio-allocations-table.component';
import { PortfolioSummaryComponent } from '../portfolio-summary/portfolio-summary.component';
import { MakeChangesModalComponent } from '../make-changes-modal/make-changes-modal.component';
import { EditPersonalInfoModalComponent } from '../edit-personal-info-modal/edit-personal-info-modal.component';
import { CashVestedModalComponent } from '../cash-vested-modal/cash-vested-modal.component';
import { RecommendationDisclaimerModalComponent } from '../recommendation-disclaimer-modal/recommendation-disclaimer-modal.component';
import { ExploreOtherOptionsComponent } from '../../routes/+explore-other-options';
import { PortfolioOptionComponent } from '../portfolio-option/portfolio-option.component';
import { ChartLegendComponent } from '../chart-legend/chart-legend.component';
import { SubmitAccordionComponent } from '../submit-accordion/submit-accordion.component';
import { FileInputComponent } from '../file-input/file-input.component';
import { FileAttachmentsComponent } from '../file-attachments/file-attachments.component';
import { FileListComponent } from '../file-list/file-list.component';
import { BottomPageErrorComponent } from '../bottom-page-error/bottom-page-error.component';
import { UploadLaterModalComponent } from '../upload-later-modal/upload-later-modal.component';
import { CashAllocationOptionComponent } from '../cash-allocation-option/cash-allocation-option.component';
import { FourtyOnePComponent } from '../fourty-one-p/fourty-one-p.component';
import { RollOverAccountsComponent } from '../roll-over-accounts/roll-over-accounts.component';

// Actions
import { ApplicationActions } from '../../actions/application.actions';
import { ApplicationHooks } from '../../actions/application.hooks';

// Strategies
import { NoHistoryStrategy } from '../../shared/strategies/nohistory.strategy';

// Pipes
import { StringReplacePipe } from '../../shared/pipes/string-replace.pipe';
import { SafePipe } from '../../shared/pipes/safe.pipe';
import { AccountMask } from '../../shared/pipes/account-mask.pipe';
import { StepperA11yPipe } from '../../shared/pipes/stepper-a11y.pipe';

// Directives
import { SetFocusDirective } from '../../shared/directives/focus.directive';
import { FocusTrapDirective } from '../../shared/directives/focus-trap.directive';
import { OccupationListResolver } from '../../resolvers/occupationList.resolver';
import { IncomeListResolver } from '../../resolvers/incomeList.resolver';

/**
 * `AppModule` is the main entry point into Angular2's bootstraping process
 */
@NgModule({
  bootstrap: [RootComponent],
  declarations: [
    AccountTypeComponent,
    AchProfileComponent,
    AchProfileFailedComponent,
    ActionHelperComponent,
    CaretArrowComponent,
    CashAllocationComponent,
    CashAllocationOptionComponent,
    CipComponent,
    CurrencyComponent,
    CheckboxComponent,
    CredentialsComponent,
    CustodialMinorInfoComponent,
    DepositFundsComponent,
    DepositFundsReceiptComponent,
    DisclaimerComponent,
    EditBeneficiaryInfoComponent,
    EmploymentfactoryComponent,
    EmploymentInfoComponent,
    ErrorSubmittingAppComponent,
    FaqComponent,
    FaqAnswerComponent,
    FieldDisplayComponent,
    FinancialInfoComponent,
    FinishPaperAccountComponent,
    FooterComponent,
    FormfactoryComponent,
    FormAccountTypeHeaderComponent,
    FundingFooterComponent,
    FundingChoicesComponent,
    FundingChoicesStreamlineComponent,
    FundAbaRoutingComponent,
    FundAccountComponent,
    FundByBrokerComponent,
    FundByBrokerChooseFirmComponent,
    FundByBrokerAccountComponent,
    FundByBrokerInfoComponent,
    FundByBrokerManualComponent,
    FundByBrokerReviewComponent,
    FundByBrokerReceiptComponent,
    FundByCheckComponent,
    FundByInvestComponent,
    FundByWireComponent,
    GetStartedComponent,
    HeaderComponent,
    HelpComponent,
    HelpTipComponent,
    IconComponent,
    InputComponent,
    InvestmentObjectiveComponent,
    InvestmentPreferencesComponent,
    TrustedContactComponent,
    JointFinancialInfoComponent,
    JointPersonalInfoComponent,
    ManualComponent,
    ManualDocsComponent,
    ModalComponent,
    NoneditableComponent,
    PageErrorComponent,
    PaperAccountComponent,
    PersonalInfoComponent,
    SSNComponent,
    PersonalAddressComponent,
    PersonalContactComponent,
    PersonalInfoAuthComponent,
    PersonalInfoACMComponent,
    PendingAppComponent,
    PendingAppCipComponent,
    PrimaryBeneficiaryInfoComponent,
    PrivacyComponent,
    ProgressBarComponent,
    RadioComponent,
    RejectedAppComponent,
    ReviewDepositFundsComponent,
    RootComponent,
    SelectComponent,
    ShowHidefactoryComponent,
    StepTitleComponent,
    SubmitApplicationComponent,
    SubmitApplicationReviewComponent,
    SuccessComponent,
    SvgLoaderComponent,
    TimeOutComponent,
    TextareaComponent,
    TooltipComponent,
    TypeAheadSearchComponent,
    ValuepropComponent,
    WaysToFundComponent,
    StringReplacePipe,
    SafePipe,
    AccountMask,
    WelcomeInvestorComponent,
    GoalComponent,
    TimeHorizonComponent,
    FinancialPictureComponent,
    RiskToleranceComponent,
    StrategyTypeComponent,
    RecommendationComponent,
    RetirementComponent,
    IconButtonComponent,
    IconRadioButtonComponent,
    RiskButtonComponent,
    QuestionnaireRightRailComponent,
    QuestionnaireTitleComponent,
    HelpMeDecideComponent,
    SetFocusDirective,
    FocusTrapDirective,
    StepperA11yPipe,
    AssetBreakdownComponent,
    CardComponent,
    AddressComponent,
    CashVestedComponent,
    PortfolioAllocationsTableComponent,
    PortfolioSummaryComponent,
    MakeChangesModalComponent,
    EditPersonalInfoModalComponent,
    CashVestedModalComponent,
    RecommendationDisclaimerModalComponent,
    ExploreOtherOptionsComponent,
    PortfolioOptionComponent,
    ChartLegendComponent,
    SubmitAccordionComponent,
    DocumentUploaderComponent,
    FileInputComponent,
    FileAttachmentsComponent,
    FileListComponent,
    BottomPageErrorComponent,
    WelcomeBackComponent,
    DocumentConfirmationReceivedComponent,
    DocumentCheckYourInboxComponent,
    UploadLaterModalComponent,
    FourtyOnePComponent,
    RollOverAccountsComponent
  ],
  imports: [
    // import Angular's modules
    BrowserModule,
    FormsModule,
    HttpClientModule,
    NgReduxModule,
    RouterModule.forRoot(ROUTES, { useHash: false }),
  ],
  providers: [
    // expose our Services and Providers into Angular's dependency injection
    ENV_PROVIDERS,
    { provide: LocationStrategy, useClass: NoHistoryStrategy },
    ACHProfileCanActivate,
    ACHTransferCanActivate,
    ApplicationActions,
    ApplicationProxyService,
    ApplicationHooks,
    BadWordsService,
    CookieService,
    CredentialsCanActivate,
    DataResolver,
    DTMGuard,
    DTMService,
    DepositAccountsResolver,
    DevToolsExtension,
    DomInjectorService,
    FlowManagerService,
    TransmitService,
    FundingService,
    GetKBACanActivate,
    HandshakeCanActivate,
    LocalStorageService,
    LoggerService,
    LogRocketTrackerService,
    MaskingService,
    ModelCanDeactivate,
    PlaidService,
    ValidationCanDeactivate,
    ModalService,
    RestService,
    ReviewResolver,
    SelectOptionsService,
    SessionStorageService,
    SubmitAppCanActivate,
    SubmitAcatsCanActivate,
    StartUpService,
    TimeOutService,
    TimeService,
    Validation,
    RouteParamsHandler,
    QuestionnaireService,
    MpQuestionnaireCanActivate,
    ScreenSizeService,
    GoalValidationService,
    PercentPipe,
    OccupationListResolver,
    IncomeListResolver,
  ],
})
export class RootModule {}
